class RoyaltyMessage extends HTMLElement {
  constructor() {
    super();
    this.royaltyMsgEl = this.querySelector('.royalty-message');
    this.causeData = JSON.parse(this.querySelector('#CauseData').innerHTML);
  }

  updateRoyaltyUnsubscriber = undefined;

  connectedCallback() {
    this.updateRoyaltyUnsubscriber = subscribe(PUB_SUB_EVENTS.updateRoyalty, (causeHandle) =>
      this.updateRoyalty(causeHandle)
    );
  }

  disconnectedCallback() {
    if (this.updateRoyaltyUnsubscriber) {
      this.updateRoyaltyUnsubscriber();
    }
  }

  /**
   * Updates the royalty message on the page based on the currently selected cause.
   * @param {String} causeHandle a handlized string of the cause name.
   */
  updateRoyalty = (causeHandle) => {
    const causeData = this.getCauseData(causeHandle);
    if (causeData.tiers.flat_donation !== 'true') {
      this.setRoyaltyMessage(causeData);
    } else {
      this.royaltyMsgEl.textContent = causeData.message;
    }
  };

  /**
   * Gets the cause data for the currently selected cause.
   * @param {String} causeHandle a handlized string of the cause name.
   * @returns The selected cause's data object.
   */
  getCauseData = (causeHandle) => {
    return this.causeData.filter((causeObj) => causeObj.handle === causeHandle)[0];
  };

  /**
   * Gets the selected variant's *current* price, not the "compare at" or
   * the original/full price. The returned price does *not* include a decimal!
   * ie: $5.00 == 500, $19.99 == 1999, $0.75 == 0075.
   * @returns The value set to the 'variantPrice' variable.
   */
  getSelectedVariantPrice = () => {
    const selectedVariantId = Number(document.querySelector('.product-variant-id').value);
    const variantPrice = Number(meta.product.variants.filter((variant) => variant.id === selectedVariantId)[0].price);
    return variantPrice;
  };

  /**
   * Gets the decimal number necessary for calculating the royalty amount
   * Math.round(((variantPrice * multiplier) / divisor) * 0.01);
   * @param {Number} variantPrice Variant price without a decimal
   * ie: $5.00 == 500, $19.99 == 1999, $0.75 == 0075.
   * @param {Object} causeData Object containing the necessary key/value pairs for
   * creating the royalty message based on the currently selected cause.
   * @returns A decimal number necessary for calculating the royalty amount.
   */
  getMultiplier = (variantPrice, causeData) => {
    let multiplier = null;
    const reversedTiersArray = Object.entries(causeData.tiers).sort((a, b) => b[1] - a[1]);

    for (const [key, value] of reversedTiersArray) {
      if (key !== 'flat_donation' && key !== 'divisor') {
        const splitExpression = key.split(' ');
        let royaltyExpression = null;
        switch (splitExpression[1]) {
          case '<':
            royaltyExpression = variantPrice < Number(splitExpression[2]);
            break;
          case '<=':
            royaltyExpression = variantPrice <= Number(splitExpression[2]);
            break;
          case '>':
            royaltyExpression = variantPrice > Number(splitExpression[2]);
            break;
          case '>=':
            royaltyExpression = variantPrice >= Number(splitExpression[2]);
            break;
          default:
            console.error('Could not formulate expression for royalty calculation.');
            break;
        }
        if (royaltyExpression) {
          multiplier = Number(value);
        }
      }
    }
    return multiplier;
  };

  /**
   * Takes the final donation number, and if larger than 1, adds an "s" in the string
   * where the '[[[plural]]]' substring exists in the royalty message. Otherwise,
   * this renmoves the placeholder substring.
   * @param {Number} finalDonation The final calculated number used in the
   * royalty message *WITHOUT* commas added.
   * @param {Object} causeData Object containing the necessary key/value pairs for
   * creating the royalty message based on the currently selected cause.
   * @returns A royalty message string with, or without, the plural form of the
   * donation word ie: "bowl" vs "bowls", "acre" vs "acres". Words whose plural
   * are the same as the singular, ie: "sheep", are not handled differently. This
   * is important to know so that the message is written in the metaobject
   * definition *WITHOUT* the '[[[plural]]]' placeholder.
   */
  constructFinalMessage = (finalDonation, causeData) => {
    let finalMessage = '';
    const finalDonationString = this.addCommas(finalDonation);
    let wrappedDonation = `<span class='donation-amount'>${finalDonationString}</span>`;
    finalMessage = causeData.message.replace('[[[amount]]]', wrappedDonation);
    if (finalDonation > 1) {
      finalMessage = finalMessage.replace('[[[plural]]]', 's');
    } else {
      finalMessage = finalMessage.replace('[[[plural]]]', '');
    }

    return finalMessage;
  };

  /**
   * Accepts the final donation amount as a number and converts it into a comma-separated string where applicable.
   * @param {Number} donationAmount The final calculated donation amount as a number.
   * @returns A comma-separated number string at each thousandths' place
   */
  addCommas = (donationAmount) => {
    const donationString = donationAmount.toString();
    if (donationString.length >= 4) {
      const regex = /\B(?=(\d{3})+(?!\d))/g;
      return donationString.replace(regex, ',');
    } else {
      return donationAmount;
    }
  };

  /**
   * Sets the text content of the royalty message element on the page to the
   * appropriate royalty message of the selected cause, complete with a calculated
   * donation amount based on the current variant and quantity.
   * @param {Object} causeData Object containing the necessary key/value pairs for
   * creating the royalty message based on the currently selected cause.
   */
  setRoyaltyMessage = (causeData) => {
    const variantPrice = this.getSelectedVariantPrice();
    const multiplier = this.getMultiplier(variantPrice, causeData);
    const divisor = Number(causeData.tiers.divisor);
    const qtyVal = document.querySelector('product-info .quantity__input').value;

    let baseDonation = Math.round(((variantPrice * multiplier) / divisor) * 0.01);
    baseDonation < 1 ? (baseDonation = 1) : null;

    const finalDonation = qtyVal * baseDonation;

    this.royaltyMsgEl.innerHTML = this.constructFinalMessage(finalDonation, causeData);
  };
}

customElements.define('royalty-message', RoyaltyMessage);
